const noop = () => {}
console.log = noop
console.info = noop
console.debug = noop
console.warn = noop
console.error = noop
console.group = noop
console.groupCollapsed = noop
console.groupEnd = noop

import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import App from './App'
import ScanCapturePage from '@components/ScanCapture/ScanCapturePage'
import LoginPage from '@components/Auth/LoginPage'
import ShopsPage from '@components/Shops/ShopsPage'
import ShopDetailPage from '@components/Shops/ShopDetailPage'
import ClaimPage from '@components/Shops/ClaimPage'
import ShopsMapPage from '@components/Shops/ShopsMapPage'
import LocationPickerPage from '@components/Shops/LocationPickerPage'
import PublicShopPage from '@components/Shops/PublicShopPage'
import EntrancePage from '@components/Entrance/EntrancePage'
import IntroPage from '@components/Intro/IntroPage'
import PublicShopBrowser from '@components/Shops/PublicShopBrowser'
import PublicShopMap from '@components/Shops/PublicShopMap'
import AuthGate from '@components/Auth/AuthGate'
import ShopAccessGate from '@components/Auth/ShopAccessGate'
import SuperUserGate from '@components/Auth/SuperUserGate'
import { BackHomeButton } from '@components/Navigation/BackHomeButton'
import BottomTabBar from '@components/BottomTabBar/BottomTabBar'
import { useAuthStore } from '@/stores/authStore'
import { apiUrl } from '@/config'
import './index.css'

// i18n + Arabic font
import './i18n'
import '@fontsource/cairo/400.css'
import '@fontsource/cairo/700.css'
import { I18nRoot } from './I18nRoot'

// Lazy-loaded delivery pages
const CheckoutPage = lazy(() => import('@/features/delivery/pages/CheckoutPage'))
const OrdersPage = lazy(() => import('@/features/delivery/pages/OrdersPage'))
const ShopDashboardPage = lazy(() => import('@/features/delivery/pages/ShopDashboardPage'))
const ChatPage = lazy(() => import('@/features/delivery/pages/ChatPage'))
const LiveTrackingPage = lazy(() => import('@/features/delivery/pages/LiveTrackingPage'))
const SharedOrderPage = lazy(() => import('@/features/delivery/pages/SharedOrderPage'))
const TripPage = lazy(() => import('@/features/delivery/pages/TripPage'))
const TripsListPage = lazy(() => import('@/features/delivery/pages/TripsListPage'))
const SharedTripPage = lazy(() => import('@/features/delivery/pages/SharedTripPage'))
const WishListPage = lazy(() => import('@/features/delivery/pages/WishListPage'))
const ProfilePage = lazy(() => import('@/components/Profile/ProfilePage'))
const MessagesPage = lazy(() => import('@/components/Messages/MessagesPage'))
const AdminDashboard = lazy(() => import('@/components/Admin/AdminDashboard'))
const AdminShopsList = lazy(() => import('@/components/Admin/AdminShopsList'))
const AdminUsersList = lazy(() => import('@/components/Admin/AdminUsersList'))
const AdminScansPage = lazy(() => import('@/components/Admin/AdminScansPage'))
const Admin3DPage = lazy(() => import('@/components/Admin/Admin3DPage'))
const AdminReviewsPage = lazy(() => import('@/components/Admin/AdminReviewsPage'))
const ShopReviewsPage = lazy(() => import('@/components/Shops/ShopReviewsPage'))
const AdminDataExplorer = lazy(() => import('@/components/Admin/AdminDataExplorer'))

// Marketplace pages (lazy)
const SearchPage = lazy(() => import('@/components/Marketplace/SearchPage'))
const ProductsPage = lazy(() => import('@/components/Marketplace/ProductsPage'))
const CouponsPage = lazy(() => import('@/components/Marketplace/CouponsPage'))
const DeliveryZonesPage = lazy(() => import('@/components/Marketplace/DeliveryZonesPage'))
const DisputesPage = lazy(() => import('@/components/Marketplace/DisputesPage'))
const DriverDashboard = lazy(() => import('@/components/Marketplace/DriverDashboard'))
const NotificationsPage = lazy(() => import('@/components/Marketplace/NotificationsPage'))
const ReturnsPage = lazy(() => import('@/components/Marketplace/ReturnsPage'))
const AdminMarketplacePage = lazy(() => import('@/components/Marketplace/AdminMarketplacePage'))

// Load debug utilities for console testing
import '@utils/debugUtils'
import { setupDebugConsole } from '@utils/debugConsole'
// Initialize theme before first render
import './stores/themeStore'

// Expose THREE.js to window for standalone 3D viewer
// This ensures the viewer in public/3d-viewer/ uses the same THREE instance
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

window.THREE = THREE
window.OrbitControls = OrbitControls

// Parse URL parameters for direct shop access
const urlParams = new URLSearchParams(window.location.search)
const shopIdFromUrl = urlParams.get('shopid')

// A `?token=` URL parameter used to be written straight into localStorage so the
// mobile app could hand the web view a session. That made any link a session
// injection: sending someone a crafted URL silently signed their browser into
// another account. Sessions now come only from Google sign-in on /login.

console.log('🔗 [main.tsx] URL parameters:', { shopid: shopIdFromUrl, fullUrl: window.location.href })

// Setup debug console
setupDebugConsole()

/**
 * Root route: shows EntrancePage by default; shows the 3D viewer when ?shopid=
 * or ?morphUrl= is present.
 *
 * The entrance page stays open to everyone — it is the landing page that offers
 * sign-in. Opening a shop from it goes through ShopAccessGate.
 */
function RootRoute() {
  const [params] = useSearchParams()
  const shopId = params.get('shopid')
  const morphUrl = params.get('morphUrl')
  if (!shopId && !morphUrl) return <EntrancePage />
  return (
    <ShopAccessGate>
      <App initialShopId={shopId} />
    </ShopAccessGate>
  )
}

function ThemeColorSync() {
  const { pathname } = useLocation()
  useEffect(() => {
    const isDark = document.documentElement.getAttribute('data-theme') !== 'light'
    const color = pathname === '/' ? '#000000' : (isDark ? '#0f172a' : '#f1f5f9')
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (meta) meta.content = color
    document.documentElement.style.removeProperty('background-color')
  }, [pathname])
  return null
}

// Hydrate auth store globally: if a session token exists in localStorage,
// restore the user profile so all pages (including public shop viewer) know the user.
;(async () => {
  const stored = localStorage.getItem('dukkan_token')
  if (stored && !useAuthStore.getState().user) {
    try {
      const res = await fetch(apiUrl('/api/auth/me'), {
        headers: { Authorization: `Bearer ${stored}` },
      })
      if (res.ok) {
        const user = await res.json()
        useAuthStore.getState().setAuth(user, stored)
        console.log('🔑 [main] Auth restored:', user.name)
      } else if (res.status === 401) {
        localStorage.removeItem('dukkan_token')
      }
      // Non-401 server errors: keep token, user will stay logged in on retry
    } catch {
      // Network error — leave unauthed, don't remove token
    }
  }

  // Site-visit ping — once per calendar day per browser session
  const today = new Date().toISOString().slice(0, 10)
  const visitKey = 'dukkan_last_visit'
  if (localStorage.getItem(visitKey) !== today) {
    try {
      const { getGuestId } = await import('./utils/guestIdentity')
      const authUser = useAuthStore.getState().user
      const visitorId = authUser?.id || getGuestId()
      fetch(apiUrl('/api/site-visit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitorId, ...(authUser?.id ? { userId: authUser.id } : {}) }),
      }).then(() => {
        localStorage.setItem(visitKey, today)
      }).catch(() => {})
    } catch {}
  }
})();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}>
  <I18nRoot>
  <BrowserRouter>
    <ThemeColorSync />
    <BackHomeButton />
    <BottomTabBar />
    <Routes>
      {/* 3D Intro experience */}
      <Route path="/intro" element={<IntroPage />} />
      {/* Entrance — "/" shows hub page UNLESS ?shopid= is present in current URL (backward compat) */}
      <Route path="/" element={<RootRoute />} />
      {/* Shop discovery + shop pages — Google sign-in required */}
      <Route path="/browse" element={<ShopAccessGate><PublicShopBrowser /></ShopAccessGate>} />
      <Route path="/map" element={<ShopAccessGate><PublicShopMap /></ShopAccessGate>} />
      <Route path="/shop/:shopId" element={<ShopAccessGate><PublicShopPage /></ShopAccessGate>} />
      <Route path="/reviews/:shopId" element={<ShopAccessGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ShopReviewsPage /></Suspense></ShopAccessGate>} />
      {/* Claim stays open: it is a one-time invite link and leads to sign-in itself */}
      <Route path="/claim/:token" element={<ClaimPage />} />
      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      {/* Owner pages (auth-gated) */}
      <Route path="/shops" element={<AuthGate><ShopsPage /></AuthGate>} />
      <Route path="/shops/map" element={<AuthGate><ShopsMapPage /></AuthGate>} />
      <Route path="/shops/:id" element={<AuthGate><ShopDetailPage /></AuthGate>} />
      <Route path="/location-picker" element={<AuthGate><LocationPickerPage /></AuthGate>} />
      <Route path="/scan" element={<AuthGate><ScanCapturePage /></AuthGate>} />
      {/* Delivery / orders (lazy) */}
      <Route path="/checkout/:shopId" element={<ShopAccessGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><CheckoutPage /></Suspense></ShopAccessGate>} />
      <Route path="/orders" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><OrdersPage /></Suspense></AuthGate>} />
      <Route path="/orders/dashboard" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><OrdersPage /></Suspense></AuthGate>} />
      <Route path="/dashboard/:shopId" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ShopDashboardPage /></Suspense></AuthGate>} />
      <Route path="/messages" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><MessagesPage /></Suspense></AuthGate>} />
      <Route path="/chat/:orderId" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ChatPage /></Suspense></AuthGate>} />
      <Route path="/tracking/:orderId" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><LiveTrackingPage /></Suspense></AuthGate>} />
      {/* Public delivery */}
      <Route path="/d/:token" element={<Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><SharedOrderPage /></Suspense>} />
      <Route path="/dt/:token" element={<Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><SharedTripPage /></Suspense>} />
      <Route path="/w/:token" element={<Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><WishListPage /></Suspense>} />
      {/* Profile */}
      <Route path="/profile" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ProfilePage /></Suspense></AuthGate>} />
      {/* Admin (superuser-gated) */}
      <Route path="/admin" element={<SuperUserGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><AdminDashboard /></Suspense></SuperUserGate>} />
      <Route path="/admin/shops" element={<SuperUserGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><AdminShopsList /></Suspense></SuperUserGate>} />
      <Route path="/admin/users" element={<SuperUserGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><AdminUsersList /></Suspense></SuperUserGate>} />
      <Route path="/admin/scans" element={<SuperUserGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><AdminScansPage /></Suspense></SuperUserGate>} />
      <Route path="/admin/3d" element={<SuperUserGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><Admin3DPage /></Suspense></SuperUserGate>} />
      <Route path="/admin/reviews" element={<SuperUserGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><AdminReviewsPage /></Suspense></SuperUserGate>} />
      <Route path="/admin/data-explorer" element={<SuperUserGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><AdminDataExplorer /></Suspense></SuperUserGate>} />
      {/* Trips (auth-gated) */}
      <Route path="/trip/:tripId" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><TripPage /></Suspense></AuthGate>} />
      <Route path="/trips" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><TripsListPage /></Suspense></AuthGate>} />
      {/* Marketplace */}
      <Route path="/search" element={<ShopAccessGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><SearchPage /></Suspense></ShopAccessGate>} />
      <Route path="/products/:shopId" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ProductsPage /></Suspense></AuthGate>} />
      <Route path="/coupons/:shopId" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><CouponsPage /></Suspense></AuthGate>} />
      <Route path="/delivery-zones/:shopId" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><DeliveryZonesPage /></Suspense></AuthGate>} />
      <Route path="/disputes" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><DisputesPage /></Suspense></AuthGate>} />
      <Route path="/driver" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><DriverDashboard /></Suspense></AuthGate>} />
      <Route path="/notifications" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><NotificationsPage /></Suspense></AuthGate>} />
      <Route path="/returns" element={<AuthGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><ReturnsPage /></Suspense></AuthGate>} />
      <Route path="/admin/marketplace" element={<SuperUserGate><Suspense fallback={<div style={{background:'var(--bg-primary)',color:'var(--text-primary)',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Loading...</div>}><AdminMarketplacePage /></Suspense></SuperUserGate>} />
      {/* Catch-all → 3D viewer. Gated too: it renders shop content, so leaving
          it open would make every gate above bypassable by an unknown path. */}
      <Route path="*" element={<ShopAccessGate><App initialShopId={shopIdFromUrl} /></ShopAccessGate>} />
    </Routes>
  </BrowserRouter>
  </I18nRoot>
  </Suspense>
)
